import { Img } from '@/components/blocks/MaterialImage';
import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { ProductDetailsDisplay } from '@/components/blocks/ProductDetails/Display';
import { ProductDetailsPrice } from '@/components/blocks/ProductDetails/Price';
import { PRODUCT_BAR_ID } from '@/components/content/ProductDetails/constants';
import { MinQuantityMsg } from '@/components/content/ProductDetails/parts/MinQuantityMsg';
import { productBarStickySX } from '@/components/content/ProductDetails/styles/StickyProductHeader/productBar';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useInventoryV2 } from '@/data/Content/InventoryV2';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useStoreIDS } from '@/data/state/useStoreIDs';
import { Attachment, Ribbon } from '@/data/types/Product';
import { getFulfillmentType } from 'shared/custom/utils/inventoryUtils';
import { Box, Paper, Stack, SxProps, Theme, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { forwardRef, useContext } from 'react';
import {
	CDN_BASE_URL,
	fulfillmentTypes,
	IMAGES,
	MATERIAL_SYMBOL_ICONS,
	MAX_QUANTITY_AVAILABLE,
	NO_IMAGE_PLACE_HOLDER,
	QUANTITY_AVAILABLE_PLUS_10,
	QUANTITY_AVAILABLE_PLUS_199
} from 'shared/custom/constants/commonConstants';
import { INVENTORY_SEARCH_PDP } from 'shared/custom/constants/inventoryConstants';

export const ProductBar = forwardRef(
	({ sx }: { sx?: SxProps<Theme> }, ref: React.Ref<HTMLDivElement>) => {
		const HDMProductDetail = useLocalization('HDMProductDetail');
		const theme = useTheme();
		const isMobile = useMediaQuery(theme.breakpoints.down('md'));
		const { selection } = useContext(ContentContext) as ReturnType<typeof useProductDetails>;
		const { productPrice, attachments, id, buyable, attributes, ribbons } = selection?.sku ?? {};

		const imageAttachment = attachments?.find(
			(attachment: Attachment) => attachment.usage === IMAGES && attachment.name.includes('-m')
		);

		const { uniqueID } = useStoreIDS();

		const { availability } = useInventoryV2({
			productIds: id,
			physicalStoreIds: [uniqueID],
			search: INVENTORY_SEARCH_PDP,
		});

		const availabilityQuantity = availability?.[0]?.x_AvailableQuantityInMarket
			? parseInt(availability?.[0]?.x_AvailableQuantityInMarket)
			: 0;

		const productDisabled = !buyable || !productPrice?.offer || !availabilityQuantity;
		const productNominalQty = Number(selection?.sku?.['x_measurements.nominalQuantity'] ?? 1);

		const buildAvailabilitySection = () => {
			if (productDisabled) {
				return null;
			}
			let availabilityLabel = HDMProductDetail.StickyHeaderAvailability.t({
				n:
					availabilityQuantity > QUANTITY_AVAILABLE_PLUS_199
						? HDMProductDetail.Availability.labels.available199.t()
						: availabilityQuantity,
			});

			const fulfillmentType = getFulfillmentType(
				attributes,
				ribbons?.map((or: Ribbon) => or.identifier)
			);

			if (availabilityQuantity > QUANTITY_AVAILABLE_PLUS_199) {
				availabilityLabel = HDMProductDetail.StickyHeaderAvailability.t({
					n: HDMProductDetail.Availability.labels.available199.t(),
				});
			}

			if (availabilityQuantity <= QUANTITY_AVAILABLE_PLUS_10) {
				availabilityLabel =
					HDMProductDetail.Availability.labels.just.t() +
					HDMProductDetail.StickyHeaderAvailability.t({ n: availabilityQuantity });
			}

			if (
				fulfillmentType === fulfillmentTypes.SPECIAL_ORDER ||
				availabilityQuantity === MAX_QUANTITY_AVAILABLE
			) {
				availabilityLabel =
					HDMProductDetail.Availability.labels.available.t() +
					HDMProductDetail.Availability.labels.forBuyOnline.t();
			}

			return (
				<Box sx={productBarStickySX.availabilitySection}>
					<DynamicIcon name={MATERIAL_SYMBOL_ICONS.devices} />
					<Typography
						component="span"
						variant="bodySubText"
						sx={productBarStickySX.availabilityLabel}
					>
						{availabilityLabel}
					</Typography>
				</Box>
			);
		};

		return (
			<Paper
				id={PRODUCT_BAR_ID}
				ref={ref}
				component="header"
				sx={[productBarStickySX.container, ...(Array.isArray(sx) ? sx : [sx])]}
				elevation={1}
			>
				{isMobile ? null : (
					<Box sx={productBarStickySX.leftContainer}>
						<Img
							sx={productBarStickySX.productImage}
							src={
								imageAttachment
									? CDN_BASE_URL + '/' + imageAttachment.identifier
									: NO_IMAGE_PLACE_HOLDER
							}
							alt={selection?.sku?.name}
							id="stickyProductImage"
						/>
						<ProductDetailsDisplay isStickyComponent />
					</Box>
				)}
				<Box sx={productBarStickySX.rightContainer}>
					{buildAvailabilitySection()}
					<Box
						display="flex"
						justifyContent="space-between"
						alignItems={isMobile ? 'none' : 'center'}
						width="100%"
						flexDirection={isMobile ? 'column' : 'row'}
					>
						{!isMobile ? (
							<Stack direction={'column'}>
								<Box pl={'10px'}>
									<MinQuantityMsg productNominalQty={productNominalQty} showMinimumQtyLabel />
								</Box>
								<Stack direction={'row'}>
									<ProductDetailsPrice isStickyComponent />
									<ProductDetailsAddToCart
										hideBar
										isIconButton
										showQuantityInput
										showAvailabilityLabel
										showMinimumQtyLabel
										isStickyComponent
									/>
								</Stack>
							</Stack>
						) : null}
						{isMobile ? (
							<Stack direction={'column'}>
								<Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
									<MinQuantityMsg productNominalQty={productNominalQty} showMinimumQtyLabel />
									<ProductDetailsPrice isStickyComponent />
								</Stack>
								<Stack>
									<ProductDetailsAddToCart
										hideBar
										isIconButton
										showQuantityInput
										showAvailabilityLabel
										showMinimumQtyLabel
										isStickyComponent
									/>
								</Stack>
							</Stack>
						) : null}
					</Box>
				</Box>
			</Paper>
		);
	}
);
